import { useTranslation } from "next-i18next";

import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";

import { Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";

import { currencies } from "@/data/currencies";

import FormControlLabel from "@/components/atoms/form-label/label";
import { Select, Option } from "@/components/atoms/select-dropdown/select";
import TextField from "@/components/atoms/text-field/text-field";
import DatePicker from "@/components/atoms/datepicker/datepicker";
import Button from "@/components/atoms/button/button";

import {
  usePrefilledStatus,
  canSkipPrefilledGroup,
} from "@/helpers/prefill-hook";
import InputFile from "@/components/atoms/input-file/input-file";

type CreateData = {
  id: number;
  amount: string;
  merchant: string;
  date: string;
  currency: string;
  amountNumber: string;
};
type TableRows = {
  [key: string]: CreateData;
};

function createData(
  id: number,
  amount: string,
  merchant: string,
  date: string,
  currency: string,
  amountNumber: string,
) {
  return { id, amount, merchant, date, currency, amountNumber };
}

type Props = {
  data?: string | string[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  setSucceededStep: (step: number) => void;
};

let isInitialPrefilled = true;

type PrefCurrencySomething = null | string | Array<string>;
type PrefAmountsSomething = null | string | Array<string>;

function TransactionsStep(props: Readonly<Props>) {
  const { data, activeStep, setActiveStep, setSucceededStep } = props;
  const { t } = useTranslation();

  const dateRef = useRef(null);

  const [rows, setRows] = useState({});
  const [count, setCount] = useState(0);

  const [currency, setCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [merchant, setMerchant] = useState("");
  const [date, setDate] = useState(null);
  const [files, setFiles] = useState<FileList | null>(null);

  const [hasPrefilled, setHasPrefilled] = useState(false);
  const [prefCurrencies, setPrefCurrencies] =
    useState<PrefCurrencySomething>(null);
  const [prefAmounts, setPrefAmounts] = useState<PrefAmountsSomething>(null);
  const [prefMerchants, setPrefMerchants] = useState<
    null | string | Array<string>
  >(null);
  const [prefDates, setPrefDates] = useState<null | string | Array<string>>(
    null,
  );

  usePrefilledStatus(
    "transaction-details[select-currency]",
    data,
    setPrefCurrencies,
  );
  usePrefilledStatus("transaction-details[amount]", data, setPrefAmounts);
  usePrefilledStatus("transaction-details[merchant]", data, setPrefMerchants);
  usePrefilledStatus("transaction-details[purchase-date]", data, setPrefDates);

  if (
    !hasPrefilled &&
    prefCurrencies &&
    prefAmounts &&
    prefMerchants &&
    prefDates &&
    !Object.keys(rows).length
  ) {
    let result: TableRows = {};

    const addTransaction = (curr: string, index: number) => {
      if (!curr.length) {
        return;
      }
      const prefAmount = Array.isArray(prefCurrencies)
        ? `${curr} ${prefAmounts[index]}`
        : `${curr} ${prefAmounts}`;
      if (Array.isArray(prefCurrencies)) {
        result[index] = createData(
          index,
          prefAmount,
          prefMerchants[index],
          prefDates[index],
          curr,
          prefAmounts[index],
        );
      } else {
        result[index] = createData(
          index,
          prefAmount,
          prefMerchants as string,
          prefDates as string,
          curr,
          prefAmounts as string,
        );
      }
    };
    if (Array.isArray(prefCurrencies)) {
      prefCurrencies.forEach((curr, index) => addTransaction(curr, index));
    } else {
      addTransaction(prefCurrencies, 0);
    }
    setRows(result);
    setHasPrefilled(true);
  }

  useEffect(() => {
    if (isInitialPrefilled) {
      if (canSkipPrefilledGroup("transactions", data, 4) && activeStep === 1) {
        setActiveStep(2);
        setSucceededStep(2);
      }
      isInitialPrefilled = false;
    }
  }, [activeStep, data, setActiveStep, setSucceededStep]);

  useEffect(() => {
    const form = (dateRef.current as unknown as HTMLInputElement).closest(
      "form",
    );

    const onSubmit = (e: SubmitEvent) => {
      if (form?.checkValidity()) {
        const formData = new FormData(form);
        const date = `${
          formData.get("transaction-details[purchase-date]") as string
        }`;
        if (date === "YYYY-MM-DD" || date === "Invalid Date") {
          e.preventDefault();
          e.stopPropagation();
          (dateRef.current as unknown as HTMLInputElement)?.focus();
        }
      }
    };

    form?.removeEventListener("submit", onSubmit);
    form?.addEventListener("submit", onSubmit);

    return () => {
      form?.removeEventListener("submit", onSubmit);
    };
  });

  const shouldFieldsBeRequired = () => {
    const hasUserFilledInAndGoneBackToAnotherPage = activeStep !== 1;
    const hasUserAddedRows = Object.keys(rows).length > 0;
    const userHasFilledInSomeFields =
      amount.trim().length !== 0 || merchant.trim().length !== 0 || !!date;
    if (hasUserFilledInAndGoneBackToAnotherPage) {
      return false;
    }
    if (files?.length) {
      return userHasFilledInSomeFields;
    }
    if (hasUserAddedRows) {
      return userHasFilledInSomeFields;
    }
    return true;
  };
  const required = shouldFieldsBeRequired();

  return (
    <>
      <FormControlLabel
        labelPlacement="top"
        control={
          <>
            {/* <FormHelperText id="transaction-date-help">
              YYYY-MM-DD
            </FormHelperText> */}
            <DatePicker
              maxDate={dayjs()}
              inputRef={dateRef}
              name="transaction-details[purchase-date]"
              inputProps={{
                required: required,
                readOnly: false,
                // "aria-describedby": "transaction-date-help",
              }}
              defaultValue={date}
              value={date}
              onChange={(newValue) => {
                setDate(newValue);
              }}
            />
          </>
        }
        label={
          <Typography variant="body1" component="span">
            {t("Purchase date")}
          </Typography>
        }
      />
      <FormControlLabel
        labelPlacement="top"
        control={
          <>
            {/* <FormHelperText id="transaction-date-help">
            0-9,|.0-9
          </FormHelperText> */}
            <TextField
              autoComplete="off"
              name="transaction-details[amount]"
              onInput={(e: any) =>
                (e.target.value = e.target.value.replaceAll(/[^0-9,.]/gi, ""))
              }
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{
                required: required,
                min: 0,
                pattern: String.raw`([0-9]+((\.|,)[0-9]+)?)|(([0-9]+,[0-9]+)+\.[0-9]+)`,
                title: t("Invalid format") || "Invalid format",
              }}
            />
          </>
        }
        label={
          <Typography variant="body1" component="span">
            {t("Amount charged on the card")}
          </Typography>
        }
      />
      {Object.keys(rows).length ? (
        <>
          <input
            type="hidden"
            name="transaction-details[select-currency]"
            value={currency}
          />
          {/* To get the correct order in the email */}
        </>
      ) : null}
      <FormControlLabel
        labelPlacement="top"
        control={
          <Select
            id="transactions_currency"
            name="transaction-details[select-currency]"
            required={required}
            value={currency}
            onChange={(e) => setCurrency((e.target as HTMLSelectElement).value)}
          >
            <Option value="" hidden disabled>
              {t("Choose currency")}
            </Option>
            {currencies.map((curr) => (
              <Option value={curr.Code} key={curr.Code}>
                {curr.Code} ({curr.Name})
              </Option>
            ))}
          </Select>
        }
        label={
          <Typography variant="body1" component="span">
            {t("Choose currency")}
          </Typography>
        }
      />
      <FormControlLabel
        labelPlacement="top"
        control={
          <TextField
            inputProps={{
              required: required,
            }}
            autoComplete="off"
            name="transaction-details[merchant]"
            value={merchant}
            onChange={(e) => setMerchant(e.target.value)}
          />
        }
        label={
          <Typography variant="body1" component="span">
            {t("Place of purchase (according to invoice or account statement)")}
          </Typography>
        }
      />
      <Button
        type={"submit"}
        variant="outlined"
        onClick={(e) => {
          const form = (dateRef.current as unknown as HTMLInputElement).closest(
            "form",
          );
          if (form?.checkValidity()) {
            e.preventDefault();
            const formData = new FormData(form);
            const date = `${
              formData.get("transaction-details[purchase-date]") as string
            }`;
            if (date !== "YYYY-MM-DD" && date !== "Invalid Date") {
              const currency = `${
                formData.get("transaction-details[select-currency]") as string
              }`;
              const prefAmount = `${currency} ${amount}`;
              if (currency && amount && merchant && date) {
                setRows({
                  ...rows,
                  [`${count}`]: createData(
                    count,
                    prefAmount,
                    merchant,
                    date,
                    currency,
                    amount,
                  ),
                });
                setCount(count + 1);
                setAmount("");
                setMerchant("");
                setDate(null);
                setCurrency("");
              }
            } else {
              (dateRef.current as unknown as HTMLInputElement)?.focus();
            }
          }
        }}
      >
        <AddIcon fontSize="small" /> {t("Add another transaction")}
      </Button>

      {Object.keys(rows).length ? (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 425 }} size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell>{t("Remove")}</TableCell>
                <TableCell>{t("Purchase date")}</TableCell>
                <TableCell>{t("Amount")}</TableCell>
                <TableCell>{t("Merchant")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(rows as TableRows).map((key) => {
                // @ts-ignore
                const row: CreateData = rows[key];
                return (
                  <TableRow
                    key={row.amount}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <IconButton
                        aria-label={`${t("Remove")} ${row.merchant} ${
                          row.amount
                        }`}
                        onClick={() => {
                          const newRows: any = { ...rows };
                          delete newRows[`${row.id}`];
                          setRows(newRows);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <label className="no-display">
                        {t("Purchase date") || "Purchase date"}
                        <input
                          type="text"
                          name="transaction-details[purchase-date]"
                          readOnly
                          value={row.date}
                          key={`transactions_date_${row.amount}_${row.merchant}`}
                        />
                      </label>
                      {row.date}
                    </TableCell>
                    <TableCell>
                      <label className="no-display">
                        {t("Amount") || "Amount"}
                        <input
                          type="text"
                          name="transaction-details[amount]"
                          readOnly
                          value={row.amountNumber}
                          key={`transactions_amount_${row.amountNumber}_${row.merchant}`}
                        />
                      </label>
                      <label className="no-display">
                        {t("Currency of the transaction") ||
                          "Currency of the transaction"}
                        <input
                          type="text"
                          name="transaction-details[select-currency]"
                          readOnly
                          value={row.currency}
                          key={`transactions_currency_${row.amount}_${row.merchant}`}
                        />
                      </label>
                      {row.amount}
                    </TableCell>
                    <TableCell>
                      <label className="no-display">
                        {t(
                          "Place of purchase (according to invoice or account statement)",
                        ) ||
                          "Place of purchase (according to invoice or account statement)"}
                        <input
                          type="text"
                          name="transaction-details[merchant]"
                          readOnly
                          value={row.merchant}
                          key={`transactions_merchant_${row.amount}_${row.merchant}`}
                        />
                      </label>
                      {row.merchant}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <Typography variant="overline">{t("Optional")}</Typography>
      <Typography variant="body1">
        {t(
          "Attach screenshots or relevant files related to the disputed transaction.",
        )}
      </Typography>
      <InputFile
        onChange={(files) => {
          setFiles(files);
        }}
        label={
          <Typography variant="body1" component="span">
            {t(
              "Ensure that the uploaded files clearly display the key details (amount, currency, merchant, date) and clearly indicate which transaction(s) you're disputing in the attachments.",
            )}
          </Typography>
        }
        name="transaction-details[attached-files]"
        multiple
        title={
          t(
            "Ensure that the uploaded files clearly display the key details (amount, currency, merchant, date) and clearly indicate which transaction(s) you're disputing in the attachments.",
          ) ||
          "Ensure that the uploaded files clearly display the key details (amount, currency, merchant, date) and clearly indicate which transaction(s) you're disputing in the attachments."
        }
      />
    </>
  );
}

export default TransactionsStep;
